import { useContext, useState, useMemo, useEffect } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { fetchIPCHistorial } from "../../../../application/arglyApi";
import { formatMonto } from "../../../../utils/rentalCalculations";
import { montoEnPeriodo } from "../../../../utils/montoHistorico";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  AreaChart, Area,
} from "recharts";

const MESES_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function Reportes() {
  const { contratosActivos, contratos, pagos, liquidaciones, aumentosAplicados } = useContext(AlquileresContext);
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [ipcData, setIpcData] = useState([]);

  useEffect(() => {
    fetchIPCHistorial().then(setIpcData).catch(() => {});
  }, []);

  // 1. Ingresos por comisiones (total comisiones mensuales)
  const ingresosData = useMemo(() => {
    const totalComision = contratosActivos.reduce((s, c) => s + (Number(c.comisionMonto) || 0), 0);
    const totalAlquileres = contratosActivos.reduce((s, c) => s + (Number(c.montoActual) || 0), 0);
    const totalPropietarios = totalAlquileres - totalComision;
    return { totalComision, totalAlquileres, totalPropietarios };
  }, [contratosActivos]);

  // 2. Distribucion por propietario (top 10)
  const porPropietario = useMemo(() => {
    const map = {};
    contratosActivos.forEach((c) => {
      const key = c.locadorNombre || "Sin locador";
      if (!map[key]) map[key] = { nombre: key, monto: 0, contratos: 0 };
      map[key].monto += Number(c.montoActual) || 0;
      map[key].contratos++;
    });
    return Object.values(map)
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 10);
  }, [contratosActivos]);

  // 3. Estado de cobros del mes seleccionado
  const cobrosEstado = useMemo(() => {
    let pagados = 0, parciales = 0, pendientes = 0;
    let montoCobrado = 0, montoEsperado = 0;
    contratosActivos.forEach((c) => {
      const pago = pagos.find((p) => p.contratoId === c.idFirebase && p.mes === mes && p.anio === anio);
      // Monto vigente en el mes consultado, no el monto actual del contrato.
      const { monto } = montoEnPeriodo(c, anio, mes, { aumentosAplicados, pago });
      montoEsperado += monto;
      if (!pago) pendientes++;
      else if (Number(pago.montoPagado) >= monto) { pagados++; montoCobrado += Number(pago.montoPagado); }
      else { parciales++; montoCobrado += Number(pago.montoPagado); }
    });
    return {
      chart: [
        { name: "Pagados", value: pagados, color: "#22C55E" },
        { name: "Parciales", value: parciales, color: "#F59E0B" },
        { name: "Pendientes", value: pendientes, color: "#EF4444" },
      ].filter((d) => d.value > 0),
      montoCobrado,
      montoEsperado,
    };
  }, [contratosActivos, pagos, aumentosAplicados, mes, anio]);

  // 4. Liquidaciones - propietarios pagados vs pendientes
  const liquidacionEstado = useMemo(() => {
    const locadorIds = new Set(contratosActivos.map((c) => c.locadorId || c.locadorNombre));
    let pagados = 0, pendientes = 0;
    locadorIds.forEach((id) => {
      const liq = liquidaciones.find((l) => (l.locadorId === id || l.locadorNombre === id) && l.mes === mes && l.anio === anio);
      if (liq) pagados++;
      else pendientes++;
    });
    return [
      { name: "Pagados", value: pagados, color: "#22C55E" },
      { name: "Pendientes", value: pendientes, color: "#EF4444" },
    ].filter((d) => d.value > 0);
  }, [contratosActivos, liquidaciones, mes, anio]);

  // 5. Contratos por estado
  const porEstado = useMemo(() => {
    const map = { ACTIVO: 0, VENCIDO: 0, RESCINDIDO: 0 };
    contratos.forEach((c) => { map[c.estado] = (map[c.estado] || 0) + 1; });
    return [
      { name: "Activos", value: map.ACTIVO, color: "#22C55E" },
      { name: "Vencidos", value: map.VENCIDO, color: "#EF4444" },
      { name: "Rescindidos", value: map.RESCINDIDO, color: "#9CA3AF" },
    ].filter((d) => d.value > 0);
  }, [contratos]);

  // 6. Distribucion por rango de monto
  const porRango = useMemo(() => {
    const rangos = [
      { label: "< $200k", min: 0, max: 200000, count: 0 },
      { label: "$200k-$400k", min: 200000, max: 400000, count: 0 },
      { label: "$400k-$600k", min: 400000, max: 600000, count: 0 },
      { label: "$600k-$800k", min: 600000, max: 800000, count: 0 },
      { label: "$800k-$1M", min: 800000, max: 1000000, count: 0 },
      { label: "> $1M", min: 1000000, max: Infinity, count: 0 },
    ];
    contratosActivos.forEach((c) => {
      const monto = Number(c.montoActual) || 0;
      const rango = rangos.find((r) => monto >= r.min && monto < r.max);
      if (rango) rango.count++;
    });
    return rangos.filter((r) => r.count > 0).map((r) => ({ name: r.label, cantidad: r.count }));
  }, [contratosActivos]);

  // 7. IPC evolution
  const ipcEvolucion = useMemo(() => {
    return ipcData
      .filter((e) => e.anio >= 2025)
      .map((e) => ({
        name: `${MESES_SHORT[e.mes - 1]} ${e.anio.toString().slice(2)}`,
        ipc: e.valor,
      }));
  }, [ipcData]);

  // 8. Top propietarios bar chart
  const topPropietariosChart = useMemo(() => {
    return porPropietario.map((p) => ({
      name: p.nombre.length > 15 ? p.nombre.slice(0, 15) + "..." : p.nombre,
      monto: p.monto,
      contratos: p.contratos,
    }));
  }, [porPropietario]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-xs">
        <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: {typeof p.value === "number" && p.value > 1000 ? formatMonto(p.value) : p.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="px-4 md:px-6 py-4 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Reportes</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Periodo:</span>
          <select
            value={mes}
            onChange={(e) => setMes(Number(e.target.value))}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          >
            {MESES_SHORT.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          >
            {[2024, 2025, 2026, 2027].map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-md">
          <p className="text-3xl font-bold">{contratosActivos.length}</p>
          <p className="text-sm opacity-90">Contratos Activos</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white shadow-md">
          <p className="text-xl font-bold">{formatMonto(ingresosData.totalAlquileres)}</p>
          <p className="text-sm opacity-90">Alquileres Mensuales</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white shadow-md">
          <p className="text-xl font-bold">{formatMonto(ingresosData.totalComision)}</p>
          <p className="text-sm opacity-90">Comisiones</p>
        </div>
        <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-5 text-white shadow-md">
          <p className="text-xl font-bold">{formatMonto(ingresosData.totalPropietarios)}</p>
          <p className="text-sm opacity-90">A Propietarios</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Propietarios */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Top Propietarios por Ingreso
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topPropietariosChart} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="monto" fill="#2589BD" radius={[0, 4, 4, 0]} name="Monto" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cobros del mes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-2">
            Estado de Cobros - {MESES_SHORT[mes - 1]} {anio}
          </h2>
          <p className="text-xs text-gray-400 mb-4">
            Cobrado: {formatMonto(cobrosEstado.montoCobrado)} de {formatMonto(cobrosEstado.montoEsperado)}
          </p>
          {cobrosEstado.chart.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={cobrosEstado.chart}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  dataKey="value"
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={true}
                >
                  {cobrosEstado.chart.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-400 py-12">Sin datos de cobros</p>
          )}
        </div>

        {/* Liquidaciones del mes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Pago a Propietarios - {MESES_SHORT[mes - 1]} {anio}
          </h2>
          {liquidacionEstado.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={liquidacionEstado}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  dataKey="value"
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={true}
                >
                  {liquidacionEstado.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-400 py-12">Sin datos</p>
          )}
        </div>

        {/* Contratos por estado */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Contratos por Estado
          </h2>
          {porEstado.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={porEstado}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  dataKey="value"
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={true}
                >
                  {porEstado.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-400 py-12">Sin datos</p>
          )}
        </div>

        {/* Distribucion por monto */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Contratos por Rango de Monto
          </h2>
          {porRango.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={porRango}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cantidad" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Contratos" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-400 py-12">Sin datos</p>
          )}
        </div>

        {/* IPC Evolution */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Evolucion IPC Mensual (%)
          </h2>
          {ipcEvolucion.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={ipcEvolucion}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => `${v}%`} />
                <defs>
                  <linearGradient id="ipcGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2589BD" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2589BD" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="ipc" stroke="#2589BD" fill="url(#ipcGrad)" strokeWidth={2} name="IPC" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-400 py-12">Sin datos de IPC</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Reportes;
